import { createFileRoute } from '@tanstack/react-router';
import { useGetQuestionsQuery } from '@/api/questions/getQuestions';
import { useGetPickemsQuery } from '@/api/pickems/getPickems';
import { SettingsPageTemplate } from '@/components/templates/SettingsPageTemplate';
import { createTableActions } from '@/utils/actionFactory';
import { useSettingsPage } from '@/hooks/useSettingsPage';
import { Plus, List, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useSearch } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState } from 'react';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';

export const Route = createFileRoute('/dashboard/questions/')({
  component: QuestionsPage,
});

function QuestionsPage() {
  const searchParams = useSearch({ from: '/dashboard/questions/' }) as { pickem_id?: number };
  const [selectedPickemId, setSelectedPickemId] = useState<number | undefined>(
    searchParams.pickem_id
  );

  const { data: pickems = [] } = useGetPickemsQuery();
  const {
    data: questions = [],
    isLoading,
    refetch,
  } = useGetQuestionsQuery(selectedPickemId ? { pickem_id: selectedPickemId } : undefined);

  const formattedQuestions = questions.map(question => ({
    ...question,
    id: question.question_id.toString(),
    label: question.question_text_en || question.question_text_fr || 'Untitled',
  }));

  const {
    searchQuery,
    setSearchQuery,
    filteredData: filteredQuestions,
    handleEdit: handleEditQuestion,
  } = useSettingsPage({
    data: formattedQuestions,
    isLoading,
    refetch,
    settingsKey: 'questions',
    editRoutePath: '/dashboard/questions/$id',
    getSearchFields: (question: any) => [
      question.question_text_en || '',
      question.question_text_fr || '',
      question.question_type || '',
    ],
  });

  const getPickemTitle = (pickemId?: string) => {
    if (!pickemId) return '-';
    const pickem = pickems.find(p => p.pickem_id === pickemId);
    return pickem?.title_en || pickem?.title_fr || `Pickem #${pickemId}`;
  };

  const columns = [
    {
      id: 'question_id',
      name: 'ID',
      className: 'w-16',
    },
    {
      id: 'question_text_en',
      name: 'Question',
      className: 'font-medium max-w-md',
      render: (question: { question_text_en?: string; question_text_fr?: string }) =>
        question.question_text_en || question.question_text_fr || 'Untitled',
    },
    {
      id: 'pickem_id',
      name: 'Pickem',
      render: (question: { pickem_id?: string }) => getPickemTitle(question.pickem_id),
    },
    {
      id: 'question_type',
      name: 'Type',
      render: (question: { question_type?: string }) =>
        question.question_type ? (
          <Badge
            variant="secondary"
            className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
          >
            {question.question_type}
          </Badge>
        ) : (
          '-'
        ),
    },
    {
      id: 'options',
      name: 'Options',
      render: (question: { options?: any[] }) => (
        <div className="flex items-center gap-2">
          <List className="h-4 w-4" />
          <span>{question.options?.length || 0}</span>
        </div>
      ),
    },
    {
      id: 'start_utc',
      name: 'Start Date',
      render: (question: { start_utc?: string }) =>
        question.start_utc ? new Date(question.start_utc).toLocaleDateString() : '-',
    },
  ];

  const actions = createTableActions({
    onEdit: handleEditQuestion,
  });

  const headerActions = (
    <div className="flex items-center gap-4">
      <Select
        value={selectedPickemId?.toString() || 'all'}
        onValueChange={value => setSelectedPickemId(value === 'all' ? undefined : Number(value))}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Filter by pickem" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Pickems</SelectItem>
          {pickems.map(pickem => (
            <SelectItem key={pickem.pickem_id} value={pickem.pickem_id.toString()}>
              {pickem.title_en || pickem.title_fr || `Pickem #${pickem.pickem_id}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Link to="/dashboard/questions/$id" params={{ id: 'new' }} search={{ pickem_id: undefined }}>
        <Button size="sm">
          <Plus className="mr-2 h-4 w-4" />
          Add Question
        </Button>
      </Link>
    </div>
  );

  return (
    <SettingsPageTemplate
      title="Questions"
      subtitle="Manage questions and answer options for pickems"
      data={filteredQuestions}
      isLoading={isLoading}
      searchValue={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder="Search questions by text or type..."
      columns={columns}
      actions={actions}
      headerActions={headerActions}
      emptyMessage="No questions found"
      emptyState={{
        icon: HelpCircle,
        title: 'No questions yet',
        description: 'Add questions to your pickems to start engaging users.',
        actionLabel: 'Add Question',
        onAction: () => handleEditQuestion({ id: 'new' } as any),
      }}
      breadcrumbs={<Breadcrumbs />}
    />
  );
}
